import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { GET_CLIENT_USERS } from "../graphql/client";
import { useCurrentProfile } from "./use-current-profile";
import { useTheme } from "./use-theme";
import { useI18n } from "./i18n-hooks";
import {
    ClientUserFieldsFragment,
    InvitationFieldsFragment,
} from "../__generated__/operations/graphql";
import { IconName } from "../components/Icon";

type UseEffectiveClientUsersParams = {
    showCurrentProfile: boolean;
};

/**
 * Used to list the colleagues items in a screen to select one.
 * @param {boolean} showCurrentProfile Also show the current clientProfile as an option (default to false)
 */
export const useEffectiveClientUsers = ({ showCurrentProfile }: UseEffectiveClientUsersParams) => {
    const currentProfile = useCurrentProfile();
    const { colors } = useTheme();
    const i18n = useI18n();
    const [selectedClientUserId, setSelectedClientUserId] = useState<string>();

    const { data: { getClientUsers = [], clientInvitationsSent = [] } = {} } = useQuery(
        GET_CLIENT_USERS,
        {
            variables: {
                clientId: currentProfile?.client?.id || "",
            },
            skip: !currentProfile?.client?.id,
        },
    );

    const clientUsers = useMemo(() => {
        const unionArray: (ClientUserFieldsFragment | InvitationFieldsFragment)[] = [
            ...getClientUsers.filter(
                (clientUser) => showCurrentProfile || clientUser.id !== currentProfile?.id,
            ),
            ...clientInvitationsSent,
        ];

        return unionArray.map((colleague) => {
            const isInvitation = "mode" in colleague;
            // An invitation sent by email (from the web app) has no phone number.
            let colleagueName = colleague.phoneNumber || (isInvitation && colleague.email) || "";
            if (colleague.firstName && colleague.lastName) {
                colleagueName = `${colleague.firstName} ${colleague.lastName}`;
            }
            if (colleague.id === currentProfile?.id) {
                colleagueName = i18n.t("screens.mission_select_signer.you", {
                    name: colleagueName,
                });
            }
            return {
                id: colleague.id,
                value: colleague.id,
                title: colleagueName,
                ...(isInvitation
                    ? {
                          icon: "talent" as IconName,
                          iconColor: colors.onSurfaceDisabled,
                      }
                    : {
                          avatarPath: (colleague as ClientUserFieldsFragment).avatarUrl,
                      }),
                selectable:
                    !isInvitation && (colleague as ClientUserFieldsFragment).canSignContracts,
                useBigAvatarOrIcon: true,
                labels: [
                    {
                        text: isInvitation ? i18n.t("client_worker_cell.invitation") : undefined,
                        mode: "warning" as const,
                    },
                    {
                        text:
                            !isInvitation &&
                            !(colleague as ClientUserFieldsFragment).canSignContracts
                                ? i18n.t("client_worker_cell.cannot_sign_contracts")
                                : undefined,
                        mode: "warning" as const,
                    },
                ],
                onPress:
                    !isInvitation && (colleague as ClientUserFieldsFragment).canSignContracts
                        ? () => {
                              setSelectedClientUserId(colleague.id);
                          }
                        : undefined,
            };
        });
    }, [
        clientInvitationsSent,
        colors.onSurfaceDisabled,
        currentProfile?.id,
        getClientUsers,
        i18n,
        showCurrentProfile,
    ]);

    return { clientUsers, selectedClientUserId };
};
